import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    LinearProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../services/api';

const SetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(true);
    const [validToken, setValidToken] = useState(false);
    const [user, setUser] = useState<any>(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!token) {
            setError('Token manquant');
            setLoading(false);
            return;
        }

        verifyToken();
    }, [token]);

    const verifyToken = async () => {
        try {
            const res = await api.get(`/api/auth/verify-token/${token}`);
            setValidToken(res.data.valid);
            setUser(res.data.user);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Token invalide ou expiré');
        } finally {
            setLoading(false);
        }
    };

    const getPasswordStrength = (pwd: string) => {
        let strength = 0;
        if (pwd.length >= 8) strength += 25;
        if (pwd.length >= 12) strength += 25;
        if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength += 25;
        if (/\d/.test(pwd)) strength += 15;
        if (/[^a-zA-Z\d]/.test(pwd)) strength += 10;
        return Math.min(strength, 100);
    };

    const passwordStrength = getPasswordStrength(password);
    const getStrengthColor = () => {
        if (passwordStrength < 40) return 'error';
        if (passwordStrength < 70) return 'warning';
        return 'success';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (password.length < 8) {
            setError('Le mot de passe doit contenir au moins 8 caractères');
            return;
        }

        if (password !== confirmPassword) {
            setError('Les mots de passe ne correspondent pas');
            return;
        }

        setSubmitting(true);
        try {
            await api.post('/api/auth/set-password', {
                token,
                password
            });

            setSuccess(true);
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Erreur lors de la définition du mot de passe');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (success) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
                <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
                    <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                    <Typography variant="h5" gutterBottom>
                        Mot de passe défini avec succès !
                    </Typography>
                    <Typography color="text.secondary">
                        Vous allez être redirigé vers la page de connexion...
                    </Typography>
                </Paper>
            </Box>
        );
    }

    if (!validToken || error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
                <Paper sx={{ p: 4, maxWidth: 500 }}>
                    <Typography variant="h5" gutterBottom color="error">
                        Lien invalide ou expiré
                    </Typography>
                    <Typography color="text.secondary" paragraph>
                        {error || 'Ce lien de définition de mot de passe n\'est plus valide.'}
                    </Typography>
                    <Typography color="text.secondary">
                        Veuillez contacter votre responsable de formation pour obtenir un nouveau lien.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
            <Paper sx={{ p: 4, maxWidth: 500, width: '100%' }}>
                <Typography variant="h4" gutterBottom>
                    Bienvenue {user?.firstName} !
                </Typography>
                <Typography color="text.secondary" paragraph>
                    Définissez votre mot de passe pour accéder à votre espace de formation.
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        label="Nouveau mot de passe"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        sx={{ mb: 2 }}
                        helperText="Minimum 8 caractères"
                    />

                    {password && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                Force du mot de passe
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={passwordStrength}
                                color={getStrengthColor()}
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                        </Box>
                    )}

                    <TextField
                        fullWidth
                        label="Confirmer le mot de passe"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        sx={{ mb: 3 }}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={submitting || !password || !confirmPassword}
                        startIcon={submitting ? <CircularProgress size={20} /> : null}
                    >
                        {submitting ? 'Enregistrement...' : 'Définir mon mot de passe'}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default SetPasswordPage;
